// src/tabs/ReservationsTab.js
import React, { useState, useMemo, useEffect } from "react";
import {
  Search,
  Filter,
  Calendar,
  Package,
  CheckSquare,
  Trash2,
  User,
  Factory,
  UserCheck,
  AlertCircle,
  Gem,
  Plus,
  X,
  Save,
  CheckCircle,
  AlertTriangle,
  ChevronDown,
  Bot, // <--- Novo ícone para identificar automação
  PenTool, // <--- Novo ícone para identificar manual
  Edit2, // <--- Importando ícone de edição
  Hammer, // <--- Novo ícone para Fábrica
  ShoppingBag, // <--- Novo ícone para PDV Showroom
} from "lucide-react";

import { reservationsService } from "../services/reservationsService";
import { normalizeText } from "../utils/formatters";
import ProductionConversionModal from "../components/modals/ProductionConversionModal";
import EngravingDisplay from "../components/production/EngravingDisplay";


import { useAuth } from "../contexts/AuthContext";
import { logAction, MODULES, getSafeUser } from "../services/logService";

// Helper robusto para converter qualquer coisa em Data JS real
const parseDate = (val) => {
  if (!val) return new Date(0);
  if (val.toDate) return val.toDate();
  if (typeof val === "string") return new Date(val);
  return new Date(val);
};

// Mapeamento local dos status da Fábrica para nomes amigáveis
const PE_STATUS_MAP = {
  pe_solicitado: "Solicitado",
  pe_imprimindo: "Imprimindo",
  pe_cura: "Cura",
  pe_fundicao: "Fundição",
  pe_fundidos: "Fundidos",
  pe_conferencia: "Conferência",
  pe_acabamento: "Acab. Ext",
  pe_cravacao: "Cravação",
  pe_polimento: "Polimento",
  pe_finalizacao: "Finalização",
};

export default function ReservationsTab({
  reservations,
  inventory = [],
  findCatalogItem,
  resSku,
  setResSku,
  resQty,
  setResQty,
  resNote,
  setResNote,
  resException,
  setResException,
  setReservations, // <--- Otimista
}) {
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [conversionData, setConversionData] = useState(null);
  const [editingData, setEditingData] = useState(null); // <--- ESTADO PRA EDIÇÃO
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // --- PAGINAÇÃO VISUAL ---
  const ITEMS_PER_PAGE = 50;
  const [visibleCount, setVisibleCount] = useState(ITEMS_PER_PAGE);

  // --- FILTROS E ORDENAÇÃO ---
  const filteredReservations = useMemo(() => {
    const filtered = reservations.filter((res) => {
      if (res.sku === "PDV-VENDA") return false;
      const matchesStatus =
        statusFilter === "all" || res.status === statusFilter;
      const search = normalizeText(searchText);

      const sku = normalizeText(res.sku || "");
      const orderMetadata = res.order_metadata || {};
      const orderDirect = res.order || {};
      
      // Mesclamos as origens, dando prioridade primária ao metadata (modos manuais)
      // mas não perdendo os dados de 'customer' se só existirem na chave 'order'
      const orderData = { 
        ...orderDirect, 
        ...orderMetadata,
        customer: { ...(orderDirect.customer || {}), ...(orderMetadata.customer || {}) }
      };
      const clientName = normalizeText(orderData?.customer?.name || "");
      const orderNum = normalizeText(orderData?.number || "");
      const createdBy = normalizeText(res.createdBy || "");
      const catalogItem = findCatalogItem ? findCatalogItem(res.sku) : null;
      const prodName = normalizeText(catalogItem?.name || "");

      const matchesSearch =
        sku.includes(search) ||
        clientName.includes(search) ||
        orderNum.includes(search) ||
        prodName.includes(search) ||
        createdBy.includes(search);

      return matchesStatus && matchesSearch;
    });

    return filtered.sort((a, b) => {
      // Prioridade: Data Original (customCreatedAt) > Data do Sistema (created) > Agora
      // Nota: PocketBase retorna 'created', Firebase retornava 'createdAt'
      const dateValA = a.customCreatedAt || a.created || a.createdAt;
      const dateValB = b.customCreatedAt || b.created || b.createdAt;
      
      const dateA = parseDate(dateValA);
      const dateB = parseDate(dateValB);
      return dateB - dateA;
    });
  }, [reservations, searchText, statusFilter, findCatalogItem]);

  // --- SEPARAÇÃO DE GRUPOS (MANUAL vs N8N vs PDV) ---
  const visibleReservations = filteredReservations.slice(0, visibleCount);

  const pdvReservations = visibleReservations.filter(
    (r) => r.source === "venda_presencial"
  );
  const autoReservations = visibleReservations.filter(
    (r) => r.source === "n8n"
  );
  const manualReservations = visibleReservations.filter(
    (r) => r.source !== "n8n" && r.source !== "venda_presencial"
  );

  const hasMore = visibleCount < filteredReservations.length;

  // Reseta paginação ao buscar
  useEffect(() => {
    setVisibleCount(ITEMS_PER_PAGE);
  }, [searchText, statusFilter]);

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + ITEMS_PER_PAGE);
  };

  // --- AÇÕES ---
  const handleCreateReservation = async (e) => {
    e.preventDefault();
    if (!resSku || !resQty) {
      alert("Preencha SKU e Quantidade.");
      return;
    }
    try {
      setLoading(true);
      await reservationsService.createReservation({
        sku: resSku.toUpperCase().trim(),
        quantity: Number(resQty),
        notes: resNote,
        exception: resException, // <--- Enviando a exceção
        createdBy: user?.name || "Sistema",
        source: "manual", // Marca explicitamente como manual
      });
      logAction(
        getSafeUser(user),
        MODULES.RESERVAS,
        "CRIAR",
        `Nova reserva criada: ${resQty}x ${resSku}`,
        { sku: resSku, qty: resQty, notes: resNote, exception: resException }
      );
      setResSku("");
      setResQty("1");
      setResNote("");
      if(setResException) setResException("");
      setIsCreating(false);
      alert("Reserva criada com sucesso!");
    } catch (error) {
      console.error(error);
      alert("Erro ao criar reserva: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmConversion = async (enrichedData) => {
    // --- ADICIONE ISTO PARA DEBUGAR ---
    console.log("📦 DADOS VINDO DO MODAL:", enrichedData);
    console.log("🚨 É EXCEÇÃO?", enrichedData.isInterceptedPE);
    console.log("📝 TEM NOTA VITAL?", enrichedData.vitalWarning);
    // ----------------------------------
    try {
      setLoading(true);
      await reservationsService.convertToOrder(enrichedData);
      logAction(
        getSafeUser(user),
        MODULES.RESERVAS,
        "CONVERTER_PRODUCAO",
        `Converteu reserva do item ${enrichedData.sku} em Pedido`,
        {
          sku: enrichedData.sku,
          originalReservationId: enrichedData.id,
          wasFromStock: enrichedData.fromStock || false,
        }
      );
      
      // Atualização Otimista
      if (setReservations) {
        setReservations(prev => prev.filter(r => r.id !== enrichedData.id));
      }

      alert(
        enrichedData.fromStock
          ? "Item retirado do estoque e ordem criada!"
          : "Enviado para Produção com sucesso!"
      );
      setConversionData(null);
    } catch (error) {
      console.error(error);
      alert("Erro ao converter: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReservationOnly = async (enrichedData) => {
    try {
      setLoading(true);
      await reservationsService.updateReservation(enrichedData.id, enrichedData);
      
      logAction(
        getSafeUser(user),
        MODULES.RESERVAS,
        "EDITAR",
        `Editou e salvou a reserva do item ${enrichedData.sku}`,
        { sku: enrichedData.sku, reservationId: enrichedData.id }
      );
      
      alert("Reserva salva com sucesso!");
      setEditingData(null);
    } catch (error) {
      console.error(error);
      alert("Erro ao salvar opções da reserva: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelection = (id) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedIds(newSet);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (
      !window.confirm(
        `Tem certeza que deseja EXCLUIR ${selectedIds.size} reservas?`
      )
    )
      return;

    try {
      setLoading(true);
      await reservationsService.deleteReservations(Array.from(selectedIds));
      
      // Atualização Otimista
      if (setReservations) {
        setReservations(prev => prev.filter(r => !selectedIds.has(r.id)));
      }

      setSelectedIds(new Set());
      alert("Reservas excluídas.");
    } catch (e) {
      alert("Erro: " + e.message);
    } finally {
      setLoading(false);
    }
  };

  // --- SUB-COMPONENTE DE RENDERIZAÇÃO DA TABELA ---
  const renderTableBlock = (items, title, icon, headerColor, borderColor) => {
    if (items.length === 0) return null;

    return (
      <div className="mb-8 animate-fade-in">
        {/* Cabeçalho do Grupo */}
        <div
          className={`flex items-center gap-2 px-4 py-2 rounded-t-xl border-t border-x ${borderColor} ${headerColor}`}
        >
          {icon}
          <h3 className="font-bold text-slate-700 text-sm uppercase">
            {title} ({items.length})
          </h3>
        </div>

        {/* Lista de Reservas (Substitui antiga Tabela) */}
        <div className={`border-x border-b ${borderColor} rounded-b-xl overflow-hidden bg-white shadow-sm flex flex-col divide-y divide-slate-100`}>
          {items.map((res) => {
            const catalog = findCatalogItem ? findCatalogItem(res.sku) : null;
            const isSelected = selectedIds.has(res.id);

            const stockCount = inventory.filter((i) => i.sku === res.sku && i.status === "in_stock").length;
            const isAvailable = stockCount > 0;

            // --- CONTROLE DE FÁBRICA (Pronta Entrega) ---
            const factoryItems = inventory.filter((i) => i.sku === res.sku && i.isPE === true && i.status !== "adjusted_out" && i.status !== "sold" && i.status !== "pe_interceptado" && i.status !== "in_stock");
            const factoryCount = factoryItems.length;
            const isProducing = factoryCount > 0 && !isAvailable; // Se tem estoque manda pro verde. Senão checa se tem fabrica
            
            let factoryBreakdown = "";
            if (isProducing) {
              const stageCounts = factoryItems.reduce((acc, obj) => {
                 acc[obj.status] = (acc[obj.status] || 0) + 1;
                 return acc;
              }, {});
              factoryBreakdown = Object.entries(stageCounts).map(
                 ([statusKey, qtd]) => `${qtd} em ${PE_STATUS_MAP[statusKey] || "Etapa"}`
              ).join(" • ");
            }

            let displayDate = "-";
            let displayTime = "";
            const rawDate = res.customCreatedAt || res.created || res.createdAt || res.dateStr;
            if (rawDate) {
              const d = parseDate(rawDate);
              if (!isNaN(d.getTime())) {
                  displayDate = d.toLocaleDateString("pt-BR");
                  displayTime = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
              }
            }

            return (
              <div 
                key={res.id} 
                className={`p-4 flex flex-col gap-3 lg:flex-row lg:p-5 lg:gap-6 lg:items-center w-full bg-white hover:bg-slate-50 transition-all ${isSelected ? "bg-purple-50/70" : ""}`}
              >
                {/* ZONA 1 & 2: DADOS DO ITEM (Esquerda/Topo) */}
                <div className="flex-1 flex flex-col w-full">
                  {/* ZONA 1: Cabeçalho Crítico */}
                  <div className="flex justify-between items-start gap-4">
                    {/* Esquerda: Checkbox + SKU + Produto */}
                    <div className="flex gap-3">
                      <div className="mt-1 shrink-0">
                        <input
                          type="checkbox"
                          className="w-5 h-5 rounded border-slate-300 text-purple-600 focus:ring-purple-500 cursor-pointer"
                          checked={isSelected}
                          onChange={() => toggleSelection(res.id)}
                        />
                      </div>
                      <div className="flex flex-col gap-0.5">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-lg font-semibold text-slate-800 tracking-tight">{res.sku}</span>
                          {/* BADGES ESTOQUE/FÁBRICA/FALTA */}
                          {isAvailable ? (
                            <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-md text-[10px] font-bold border border-emerald-200 shadow-sm">
                              <CheckCircle size={10} /> {stockCount} UN
                            </span>
                          ) : isProducing ? (
                             <div className="group relative">
                                <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md text-[10px] font-bold border border-blue-200 shadow-sm cursor-help hover:bg-blue-200 transition-colors">
                                  <Hammer size={10} className="text-blue-500"/> FÁBRICA ({factoryCount})
                                </span>
                                {/* Tooltip Flutuante */}
                                <div className="absolute top-full left-0 mt-1 hidden group-hover:block w-max max-w-[200px] z-[60] p-2 bg-slate-800 text-white text-[10px] rounded shadow-lg font-mono leading-tight whitespace-normal border border-slate-700">
                                    <div className="text-blue-300 font-bold mb-1 uppercase tracking-wider text-[9px]">Na Linha de Produção:</div>
                                    <div className="text-slate-100 text-xs">{factoryBreakdown}</div>
                                </div>
                             </div>
                          ) : (
                            <span className="inline-flex items-center gap-1 bg-red-100 text-red-700 px-2 py-0.5 rounded-md text-[10px] font-bold border border-red-200 shadow-sm animate-pulse">
                              <AlertTriangle size={10} /> FALTA
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 line-clamp-1 font-medium">{catalog?.name || "Produto não identificado"}</p>
                      </div>
                    </div>

                    {/* Direita: Badge de Data (Idade) */}
                    <div className="text-right shrink-0">
                       <div className="flex flex-col items-end">
                          <span className="bg-slate-100 px-2 py-1.5 rounded text-xs font-bold text-slate-600 border border-slate-200 flex items-center gap-1.5 shadow-sm">
                            <Calendar size={12} className="text-slate-400"/>
                            {displayDate}
                          </span>
                          <span className="text-[10px] font-medium text-slate-400 mt-1 mr-1">{displayTime}</span>
                       </div>
                    </div>
                  </div>

                  {/* ZONA 2: Agrupamento de Atributos (Badges/Specs) */}
                  <div className="flex flex-wrap gap-2 pt-3 mt-3 border-t border-slate-100 items-center">
                    {res.specs?.size && res.specs.size !== "ND" && (
                      <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded border border-slate-200 shadow-sm">
                        <span className="font-bold text-xs text-slate-700">Aro {res.specs.size}</span>
                      </div>
                    )}
                    {res.specs?.stoneType && res.specs.stoneType !== "ND" && (
                      <div className="flex items-center gap-1.5 bg-yellow-50 px-2 py-1 rounded border border-yellow-100 shadow-sm text-yellow-800">
                        <Gem size={12} className="text-yellow-600" />
                        <span className="font-bold text-xs tracking-tight">{res.specs.stoneType}</span>
                      </div>
                    )}
                    {res.specs?.stoneColor && res.specs.stoneColor !== "ND" && (
                      <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-1 rounded border border-slate-200 shadow-sm font-bold text-xs text-slate-700">
                        <span className="uppercase tracking-tight">{res.specs.stoneColor}</span>
                      </div>
                    )}
                    {res.specs?.finishing && res.specs.finishing !== "ND" && (
                      <span className="bg-slate-100 text-slate-600 px-1.5 py-1 rounded text-[10px] border border-slate-200 shadow-sm uppercase font-bold">
                        Fin: {res.specs.finishing}
                      </span>
                    )}
                    {res.specs?.engraving && res.specs.engraving !== "ND" && (
                      <div className="ml-auto">
                        <EngravingDisplay rawText={res.specs.engraving} mode="compact" />
                      </div>
                    )}
                    {!res.specs?.size && !res.specs?.stoneType && !res.specs?.finishing && !res.specs?.engraving && (
                      <span className="text-[10px] text-slate-400 italic flex items-center gap-1">
                        <AlertCircle size={10} /> Sem specs registrados
                      </span>
                    )}
                  </div>
                </div>

                {/* ZONA 3: RODAPÉ DO ITEM (Direita/Baixo) */}
                <div className="w-full lg:w-72 xl:w-96 shrink-0 lg:border-l lg:border-t-0 border-t border-slate-100 lg:pl-6 pt-3 lg:pt-0 mt-2 lg:mt-0 flex flex-col gap-3">
                  {/* Cliente e Pedido */}
                  <div className="flex items-center gap-2 text-sm text-slate-500 truncate w-full">
                    <User size={14} className="shrink-0 text-slate-400" />
                    <span 
                      className="font-bold text-slate-700 truncate" 
                      title={res.order_metadata?.customer?.name || res.order?.customer?.name}
                    >
                      {res.order_metadata?.customer?.name || res.order?.customer?.name || "Balcão"}
                    </span>
                    <span className="font-mono text-xs bg-slate-50 border border-slate-100 px-1.5 rounded ml-auto shrink-0">
                      Ped: {res.order_metadata?.number || res.order?.number || "-"}
                    </span>
                  </div>

                  {/* Segunda linha: Criador + Ações */}
                  <div className="flex justify-between items-center gap-2">
                    
                    {/* Badge de Origem */}
                    <div className="inline-flex items-center gap-1 bg-slate-100 px-2 py-1.5 rounded border border-slate-200 text-[10px] font-bold text-slate-600 uppercase shadow-sm truncate max-w-[120px]">
                      {res.source === "n8n" ? (
                        <><Bot size={12} className="text-orange-500 shrink-0" /> IMPORTADO</>
                      ) : res.source === "venda_presencial" ? (
                        <><ShoppingBag size={12} className="text-purple-500 shrink-0" /> PDV SHOWROOM</>
                      ) : (
                        <><UserCheck size={12} className="text-blue-500 shrink-0" /> <span className="truncate">{res.createdBy || "MANUAL"}</span></>
                      )}
                    </div>

                    {/* Botões de Ação */}
                    <div className="flex flex-wrap lg:flex-nowrap justify-end gap-2 items-center shrink-0 w-full lg:w-auto mt-2 lg:mt-0">
                      <button
                        onClick={() => setEditingData(res)}
                        className="flex-1 lg:flex-none p-2 text-slate-500 hover:text-blue-700 hover:bg-blue-50 border border-slate-200 rounded-lg transition-all flex justify-center items-center shadow-sm bg-white"
                        title="Editar Reserva"
                      >
                        <Edit2 size={16} />
                      </button>
                      
                      <button
                        onClick={() => setConversionData(res)}
                        className="flex-1 lg:flex-none p-2 text-white bg-slate-800 hover:bg-purple-700 rounded-lg transition-all flex justify-center items-center shadow-sm"
                        title="Enviar para Fábrica"
                      >
                        <Factory size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-slate-50 overflow-hidden relative">
      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 bg-white/50 z-50 flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white p-4 rounded-xl shadow-xl flex items-center gap-3">
            <div className="animate-spin w-6 h-6 border-4 border-purple-600 border-t-transparent rounded-full"></div>
            <span className="font-bold text-purple-800">Processando...</span>
          </div>
        </div>
      )}

      {/* Modais */}
      {/* Modal 1: Converter para Produção */}
      {conversionData && (
        <ProductionConversionModal
          isOpen={!!conversionData}
          reservation={conversionData}
          onClose={() => setConversionData(null)}
          onConfirm={handleConfirmConversion}
          findCatalogItem={findCatalogItem}
          inventory={inventory}
        />
      )}

      {/* Modal 2: Editar e Salvar Reserva (SEM CONVERTER) */}
      {editingData && (
        <ProductionConversionModal
          isOpen={!!editingData}
          reservation={editingData}
          onClose={() => setEditingData(null)}
          onConfirm={handleSaveReservationOnly} // No modo edição, o submit principal apenas salva
          findCatalogItem={findCatalogItem}
          inventory={inventory}
          isSaveOnlyMode={true} // Diz ao modal para mascarar os textos de fábrica
        />
      )}

      {isCreating && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm animate-fade-in">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl overflow-hidden transform transition-all scale-100">
            {/* Header */}
            <div className="bg-purple-600 p-4 flex justify-between items-center text-white">
              <h3 className="font-bold flex items-center gap-2 text-lg">
                <Plus size={22} /> Nova Reserva
              </h3>
              <button
                onClick={() => setIsCreating(false)}
                className="hover:bg-white/20 p-1.5 rounded-full transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                setResQty("1");
                handleCreateReservation(e);
              }}
              className="p-6 space-y-5"
            >
              {/* Aviso Quantidade Única */}
              <div className="bg-blue-50 text-blue-700 text-xs p-3 rounded-lg flex items-start gap-2 border border-blue-100">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <p>
                  A reserva será de <b>1 unidade</b>. Se precisar reservar mais
                  itens, crie uma nova reserva após esta.
                </p>
              </div>

              {/* Campo SKU */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-500 uppercase ml-1">
                  SKU <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Package
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    size={18}
                  />
                  <input
                    autoFocus
                    type="text"
                    className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl font-bold text-slate-700 focus:border-purple-500 focus:ring-4 focus:ring-purple-50 outline-none transition-all placeholder:text-slate-300"
                    value={resSku}
                    onChange={(e) => setResSku(e.target.value)}
                    placeholder="Ex: ANEL-01"
                  />
                </div>
              </div>

              {/* Campo Obs */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-500 uppercase ml-1">
                  Observações / Cliente
                </label>
                <textarea
                  className="w-full p-3 border border-slate-200 rounded-xl text-sm resize-none h-24 focus:border-purple-500 focus:ring-4 focus:ring-purple-50 outline-none transition-all placeholder:text-slate-300"
                  value={resNote}
                  onChange={(e) => setResNote(e.target.value)}
                  placeholder="Nome do cliente, detalhes do pedido..."
                />
              </div>

              {/* Campo Exceção Vital */}
              <div className="space-y-1.5">
                <label className="block text-[10px] font-bold text-red-600 uppercase ml-1 flex items-center gap-1">
                  <AlertCircle size={12} /> Exceção / Aviso Vital
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 border border-red-200 bg-red-50 text-red-700 rounded-xl font-bold text-sm focus:border-red-500 focus:ring-4 focus:ring-red-50 outline-none transition-all placeholder:text-red-300 placeholder:font-normal"
                  value={resException || ""}
                  onChange={(e) => setResException && setResException(e.target.value)}
                  placeholder="Peça Sob Encomenda, Modificação Específica..."
                />
              </div>

              {/* Botões */}
              <div className="pt-2 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsCreating(false)}
                  className="px-5 py-2.5 border border-slate-200 text-slate-500 rounded-xl text-sm font-bold hover:bg-slate-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-purple-600 text-white rounded-xl text-sm font-bold flex items-center gap-2 hover:bg-purple-700 shadow-lg shadow-purple-200 transition-all transform active:scale-95"
                >
                  <Save size={18} /> Salvar Reserva
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div className="bg-white p-4 border-b border-slate-200 flex flex-col md:flex-row gap-4 justify-between items-start md:items-center shrink-0 shadow-sm z-20">
        <div className="flex items-center gap-2 text-slate-700">
          <Package className="text-purple-600" />
          <h2 className="font-bold text-lg hidden md:block">
            Reservas & Pedidos
          </h2>
          <h2 className="font-bold text-lg md:hidden">Reservas</h2>
          <span className="bg-slate-100 px-2 py-0.5 rounded text-xs font-bold text-slate-500">
            {filteredReservations.length}
          </span>
        </div>

        <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
          <div className="grid grid-cols-2 gap-2 md:flex">
            <button
              onClick={() => setIsCreating(true)}
              className="flex items-center justify-center gap-2 bg-purple-600 text-white px-3 py-2 rounded-lg text-sm font-bold hover:bg-purple-700 transition-colors shadow-sm"
            >
              <Plus size={16} /> <span className="md:inline">Nova</span>
            </button>

            {selectedIds.size > 0 && (
              <button
                onClick={handleBulkDelete}
                className="flex items-center justify-center gap-2 bg-red-100 text-red-700 px-3 py-2 rounded-lg text-sm font-bold hover:bg-red-200 transition-colors"
              >
                <Trash2 size={16} />{" "}
                <span className="md:inline">Excluir ({selectedIds.size})</span>
              </button>
            )}
          </div>

          <div className="flex gap-2 w-full md:w-auto">
            <div className="relative flex-1 md:w-64">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                size={16}
              />
              <input
                type="text"
                placeholder="Buscar..."
                className="pl-9 pr-4 py-2 border rounded-lg text-sm outline-none w-full focus:border-purple-500"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ÁREA DE LISTAGEM */}
      <div className="flex-1 overflow-y-auto custom-scrollbar p-2 md:p-6 bg-slate-50">
        {visibleReservations.length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            <Package size={48} className="mx-auto mb-2 opacity-20" />
            <p>Nenhuma reserva encontrada.</p>
          </div>
        ) : (
          <>
            {/* Bloco 1: Manuais */}
            {renderTableBlock(
              manualReservations,
              "Criadas Manualmente",
              <PenTool size={18} className="text-blue-600" />,
              "bg-blue-50/50",
              "border-blue-100"
            )}

            {/* Bloco 2: Automáticas (iSet) */}
            {renderTableBlock(
              autoReservations,
              "Importadas Automáticas (iSet)",
              <Bot size={18} className="text-orange-600" />,
              "bg-orange-50/50",
              "border-orange-100"
            )}

            {/* Bloco 3: Vendas Showroom (PDV) */}
            {renderTableBlock(
              pdvReservations,
              "Vendas Showroom (PDV)",
              <ShoppingBag size={18} className="text-purple-650" style={{ color: "#9333EA" }} />,
              "bg-purple-50/50",
              "border-purple-100"
            )}
          </>
        )}

        {/* BOTÃO CARREGAR MAIS */}
        {hasMore && (
          <div className="p-6 flex justify-center pb-20">
            <button
              onClick={handleLoadMore}
              className="flex items-center gap-2 bg-white border border-slate-300 text-slate-600 font-bold py-2 px-6 rounded-full hover:bg-slate-100 hover:text-purple-600 transition-all shadow-sm"
            >
              <ChevronDown size={16} />
              Carregar Mais ({filteredReservations.length - visibleCount}{" "}
              restantes)
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
